import { createSelector } from 'reselect';
import { reduce } from '@beef/legacy-ui-kit/utils';

import { convertListToObjectBy } from 'utils/fn';
import { getSearchQuery } from 'store/common/navigation/selectors';
import { selectIsSemanticUrlFilters } from 'store/common/settings/selectors';

import { getCatalogData, getCatalogBaseUrl, getCatalogHeadline } from '../selectors';
import { FILTER_SLUG_PRICE, FILTER_SLUG_BRAND } from './constants';
import { getCatalogActiveSortById, getCatalogFiltersBarChangedState } from '../controls/selectors';
import { getCatalogTotalCount } from '../products/selectors';
import {
  CATALOG_FILTERS_OPTIONS_SHOW_COUNT,
  CATALOG_FILTERS_TYPE_COLOR,
  CATALOG_FILTERS_TYPE_COMMON,
  CATALOG_FILTERS_TYPE_RANGE,
  CATALOG_MAX_PRICE,
} from '../constants';

/** selector for filters data from data converter */
export const getTagsList = (state) => getCatalogData(state).tags || [];
export const getFiltersArray = (state) => getCatalogData(state).filters || [];
export const getFiltersDict = createSelector(getFiltersArray, convertListToObjectBy('slug'));

/** selector for case when no options counts available */
export const getNoOptionsCountersFlag = (state) => getCatalogData(state).filtersWithoutCounters;

/** object of all mapped filters settings */
export const getAllFiltersSettings = createSelector(getFiltersArray, (filters) => {
  const filtersSettings = {};
  if (!filters || filters.length === 0) return {};
  filters.forEach((filter) => {
    if (filter.type === CATALOG_FILTERS_TYPE_RANGE) {
      filtersSettings[filter.slug] = {
        valueFrom: null,
        valueTo: null,
        ...filter,
      };
    } else if (
      filter.type === CATALOG_FILTERS_TYPE_COMMON ||
      filter.type === CATALOG_FILTERS_TYPE_COLOR
    ) {
      filtersSettings[filter.slug] = {
        activeOptions: filter.options
          .map(({ isChecked, slug }) => (isChecked ? slug : null))
          .filter((item) => item !== null),
        optionsToShowCount: CATALOG_FILTERS_OPTIONS_SHOW_COUNT,
        id: filter.intagId,
        ...filter,
      };
    }
  });

  return filtersSettings;
});

/** selectors for data specified for each filter, selected by slug */
export const getFilterSettings = (state, slug) => getAllFiltersSettings(state)[slug] || {};

export const getFilterBrands = createSelector(
  getCatalogBaseUrl,
  (state) => getFilterSettings(state, FILTER_SLUG_BRAND).options,
  (baseUrl, options = []) => options.map(({ name, slug }) => ({ name, url: `${baseUrl}${slug}/` })),
);

export const getFilterBrandsAvailability = (state) => !!getFilterBrands(state).length;

// ===
export const getFilterOpenedState = (state, slug) => getFilterSettings(state, slug).isOpen;
export const getFilterActiveOptions = (state, slug) => getFilterSettings(state, slug).activeOptions;
export const getAllFiltersActiveOptions = createSelector(
  (state) => state,
  getAllFiltersSettings,
  (state, filters) =>
    Object.keys(filters)
      .filter((slug) => filters[slug].type !== CATALOG_FILTERS_TYPE_RANGE)
      .map((slug) => ({
        slug,
        activeOptions: getFilterActiveOptions(state, slug),
      })),
);
export const getAllFiltersActiveOptionsCount = createSelector(
  getAllFiltersActiveOptions,
  (allActiveOptions) =>
    allActiveOptions.reduce((prev, current) => prev + current.activeOptions.length, 0),
);
export const getFilterOptionsToShowCount = (state, slug) =>
  getFilterSettings(state, slug).optionsToShowCount;
export const getFilterName = (state, slug) => getFilterSettings(state, slug).name;
/** selectors for filter options count */
export const getFilterOptions = (state, slug) => getFilterSettings(state, slug).options || [];
export const getFilterOptionsCount = (state, slug) => getFilterOptions(state, slug).length;
/** selectors for specified range filter, selected by slug */
export const getFilterRangeMin = (state) => getFilterSettings(state, FILTER_SLUG_PRICE).min;
export const getFilterRangeMax = (state) => getFilterSettings(state, FILTER_SLUG_PRICE).max;
export const getFilterRangeMaxLength = (state) =>
  Math.max((getFilterRangeMax(state) || '').toString().length, 6);
export const getFilterRangeValueFromRaw = (state) =>
  getFilterSettings(state, FILTER_SLUG_PRICE).valueFrom;
export const getFilterRangeValueToRaw = (state) =>
  getFilterSettings(state, FILTER_SLUG_PRICE).valueTo;
export const getFilterRangeUsed = (state) => getFilterSettings(state, FILTER_SLUG_PRICE).used;

export const getFilterRangeValueFrom = createSelector(
  getFilterRangeValueFromRaw,
  getFilterRangeMax,
  (valueFrom, max) => {
    if (valueFrom === null) return 0;
    if (valueFrom > max) return max;
    return valueFrom;
  },
);

export const getFilterRangeValueTo = createSelector(
  getFilterRangeValueToRaw,
  getFilterRangeMin,
  (valueTo, min) => {
    if (valueTo === null) return CATALOG_MAX_PRICE;
    if (valueTo < min) return min;
    return valueTo;
  },
);

/** external use selectors */
export const getCatalogFiltersPriceChanged = (state) =>
  getFilterRangeValueFromRaw(state) !== null || getFilterRangeValueToRaw(state) !== null;

/** selectors for SEO compatibility in url formatting, used for filters exceptions */
export const getAllFiltersSeoAllowedDict = createSelector(getFiltersDict, (dict) =>
  reduce(
    dict,
    (accum, value, slug) => ({
      ...accum,
      [slug]: value.isSeoUrlAllowed,
    }),
    {},
  ),
);
export const getIsFilterSeoUrlAllowed = (state, slug) =>
  getAllFiltersSeoAllowedDict(state)[slug] || false;

export const getIsActiveFiltersWithoutSeoUrlAllowExists = createSelector(
  getAllFiltersActiveOptions,
  getAllFiltersSeoAllowedDict,
  getCatalogFiltersPriceChanged,
  (allActiveOptions, seoUrlAllowDict, isPriceFilterChanged) =>
    allActiveOptions.reduce(
      (prev, current) =>
        prev ||
        (current.activeOptions.length !== 0 && !seoUrlAllowDict[current.slug]) ||
        isPriceFilterChanged,
      false,
    ),
);
export const getIsActiveFiltersSeoUrlAllowed = (state) =>
  !getIsActiveFiltersWithoutSeoUrlAllowExists(state);

/* */
export const getCatalogActiveFiltersData = createSelector(getAllFiltersSettings, (filters) =>
  Object.keys(filters).reduce((result, slug) => {
    const filter = filters[slug];
    if (filter.activeOptions && filter.activeOptions.length) {
      return [
        ...result,
        {
          IntagId: filter.id,
          ChoiceSlugs: filter.activeOptions,
          Slug: slug,
        },
      ];
    }
    return result;
  }, []),
);
/** selector for filters clear button show/hidden state */
export const getIsCatalogAnyFilterChanged = createSelector(
  getAllFiltersActiveOptionsCount,
  getCatalogFiltersPriceChanged,
  getCatalogFiltersBarChangedState,
  (activeOptionsCount, isPriceFilterChanged, isFiltersChanged) =>
    isFiltersChanged || isPriceFilterChanged || activeOptionsCount !== 0,
);
/**
 * Selector serializes filter and controls data into query,
 * although selector is located in `filters` section, it uses `controls` selectors,
 * if in the end it results in circular dependencies, it must be moved in it's own separate catalog-level file
 */
const collectExplicitOptionsPart = (
  activeOptionsCount,
  sortById,
  isPriceChanged,
  priceFrom,
  priceTo,
  hasTechQuery,
) =>
  []
    .concat(
      [...(activeOptionsCount === 0 && (sortById !== 0 || isPriceChanged) ? '?' : '')],
      [
        ...(sortById !== 0 ? [`sort=${sortById}`] : []),
        ...(isPriceChanged ? [`price=${priceFrom || 0}:${priceTo || 0}`] : []),
        ...((sortById !== 0 || isPriceChanged) && hasTechQuery ? [''] : []),
      ].join('&'),
    )
    .join('');

const getExplicitOptionsPartData = createSelector(
  getAllFiltersActiveOptionsCount,
  getCatalogActiveSortById,
  getCatalogFiltersPriceChanged,
  getFilterRangeValueFrom,
  getFilterRangeValueTo,
  (activeOptionsCount, sortById, isPriceChanged, priceFrom, priceTo) => ({
    hasExplicitOptions: sortById !== 0 || isPriceChanged,
    getExplicitOptionsPart: (hasTechQuery) =>
      collectExplicitOptionsPart(
        activeOptionsCount,
        sortById,
        isPriceChanged,
        priceFrom,
        priceTo,
        hasTechQuery,
      ),
  }),
);

export const getCatalogFilterAwareQuery = createSelector(
  getAllFiltersActiveOptionsCount,
  getAllFiltersSettings,
  getIsActiveFiltersSeoUrlAllowed,
  getExplicitOptionsPartData,
  selectIsSemanticUrlFilters,
  (
    activeOptionsCount,
    filters,
    seoUrlIsAllowed,
    { hasExplicitOptions, getExplicitOptionsPart },
    isFeatureFlagSemanticUrl,
  ) =>
    (hasTechQuery) => {
      if (isFeatureFlagSemanticUrl) {
        return (
          Object.keys(filters)
            .slice()
            // eslint-disable-next-line no-unsafe-optional-chaining
            .sort((slugA, slugB) => filters?.[slugB]?.weight - filters?.[slugA]?.weight)
            .reduce((result, slug) => {
              const filter = filters[slug];
              const filterHasAO = Boolean(filter.activeOptions && filter.activeOptions.length);
              if (filterHasAO) {
                const isLastAO = activeOptionsCount === result.length + 1;
                const shouldHaveLastChar = isLastAO && (hasExplicitOptions || hasTechQuery);
                return [
                  ...result,
                  `${filter.activeOptions.join('/')}/`,
                  ...(shouldHaveLastChar ? ['?'] : []),
                ];
              }
              return result;
            }, [])
            .join('')
            .concat(getExplicitOptionsPart(hasTechQuery))
        );
      }

      return Object.keys(filters)
        .reduce((result, slug) => {
          const filter = filters[slug];
          // conditions constants
          const moreThenOneAO = activeOptionsCount > 1;
          const oneAO = activeOptionsCount === 1;
          const filterHasAO = filter.activeOptions && filter.activeOptions.length;
          const filterHasOneAO = filterHasAO && filter.activeOptions.length === 1;
          // ===
          if ((moreThenOneAO && filterHasAO) || (filterHasAO && !seoUrlIsAllowed)) {
            const isLastAO = activeOptionsCount <= result.length + filter.activeOptions.length;
            const shouldHaveLastChar = isLastAO && hasExplicitOptions;
            return [
              ...(result.length === 0 ? ['?'] : [...result, '&']),
              `${slug}=${filter.activeOptions}`,
              ...(shouldHaveLastChar ? ['&'] : []),
            ];
          }
          if (oneAO && filterHasOneAO && seoUrlIsAllowed) {
            const isLastAO = activeOptionsCount === result.length + 1;
            const shouldHaveLastChar = isLastAO && (hasExplicitOptions || hasTechQuery);
            return [...result, `${filter.activeOptions}/`, ...(shouldHaveLastChar ? ['?'] : [])];
          }
          return result;
        }, [])
        .join('')
        .concat(getExplicitOptionsPart(hasTechQuery));
    },
);
/* Uncomment, when full SEO urls mechs will be needed */
// export const getCatalogFilterAwareQuery = createSelector(
//   getAllFiltersActiveOptionsCount,
//   getAllFiltersSettings,
//   getIsActiveFiltersSeoUrlAllowed,
//   getExplicitOptionsPartData,
//   (activeOptionsCount, filters, seoUrlIsAllowed, { hasExplicitOptions, getExplicitOptionsPart }) => hasTechQuery => (
//     Object.keys(filters).reduce((result, slug) => {
//       const filter = filters[slug];
//       // conditions constants
//       const moreThenTwoAO = activeOptionsCount > 2;
//       const oneOrTwoAO = activeOptionsCount <= 2 && activeOptionsCount !== 0;
//       const filterHasAO = filter.activeOptions && filter.activeOptions.length;
//       const filterHasOneAO = filterHasAO && filter.activeOptions.length === 1;
//       const filterHasSomeAO = filterHasAO && filter.activeOptions.length > 1;
//       // ===
//       if ((moreThenTwoAO && filterHasAO)
//           || (oneOrTwoAO && filterHasSomeAO)
//           || (filterHasAO && !seoUrlIsAllowed)) {
//         const isLastAO = activeOptionsCount <= (result.length + filter.activeOptions.length);
//         const shouldHaveLastChar = isLastAO && hasExplicitOptions;
//         return [
//           ...result.length === 0 ? ['?'] : [...result, '&'],
//           `${slug}=${filter.activeOptions}`,
//           ...shouldHaveLastChar ? ['&'] : [],
//         ];
//       } else if ((oneOrTwoAO && filterHasOneAO) && seoUrlIsAllowed) {
//         const isLastAO = activeOptionsCount === (result.length + 1);
//         const shouldHaveLastChar = isLastAO && (hasExplicitOptions || hasTechQuery);
//         return [
//           ...result,
//           `${filter.activeOptions}/`,
//           ...shouldHaveLastChar ? ['?'] : [],
//         ];
//       }
//       return result;
//     }, [])
//       .join('')
//       .concat(getExplicitOptionsPart(hasTechQuery))
//   ),
// );

/**
 * returns function composed by page,
 * that returns current filterAware query with pagination support
 */
export const getCatalogFullQueryComposer = createSelector(
  getCatalogFilterAwareQuery,
  getSearchQuery,
  (getFilterQuery, searchQuery) => (page) => {
    const filterQuery = [
      ...(getFilterQuery(page > 1 || searchQuery) ?
        [getFilterQuery(page > 1 || searchQuery)]
      : [...(page > 1 || searchQuery ? '?' : '')]),
    ].join('');

    const pageOrSearchQuery = [
      ...(page > 1 ? `page=${page}` : []),
      ...(searchQuery ? `query=${searchQuery}` : []),
    ].join('');

    return filterQuery.concat(pageOrSearchQuery);
  },
);

/**
 * Adds BaseUrl to FullQueryStringComposer
 */
export const getCatalogFullUrlComposer = createSelector(
  getCatalogBaseUrl,
  getCatalogFullQueryComposer,
  getTagsList,
  (baseUrl, getQueryStringByPage, tags) => (page) => {
    const activeTags = tags.filter((tag) => tag.isChecked);
    const activeTag = activeTags[0] ? `tag/${activeTags[0].slug}/` : '';
    const queryString = getQueryStringByPage(page);
    return baseUrl + activeTag + (queryString ? `${queryString}` : '');
  },
);

export const getCatalogProductsTag = createSelector(getTagsList, (tags) => () => {
  const activeTags = tags.filter((tag) => tag.isChecked);
  const activeTag = activeTags[0];
  return activeTag ? activeTag.slug : null;
});

/**
 * current state filters selectors
 */

export const getCurrentFilterSectionName = createSelector(
  getFiltersArray,
  (filtersArray) => (filterSlug) => {
    const currentFiltersArray = filtersArray.filter((filter) => filter.slug === filterSlug);

    return (currentFiltersArray[0] || {}).name;
  },
);

export const currentFilterChoiceName = createSelector(
  getFiltersArray,
  (filtersArray) => (filterSlug, optionSlug) => {
    const currentFiltersArray = filtersArray.filter((filter) => filter.slug === filterSlug);
    const currentFilterOptions = currentFiltersArray[0].options || [];
    const currentFilterOption = currentFilterOptions.filter((option) => option.slug === optionSlug);

    return (currentFilterOption[0] || {}).name;
  },
);

export const getCatalogHasFilters = (state) => getFiltersArray(state).length > 0;

/** 444 filters show/hide */
export const getCatalogShowFilters = (state) => getCatalogData(state).showFilters;
export const getCatalogFiltersAvailability = createSelector(
  getFiltersArray,
  getCatalogShowFilters,
  (filtersArray, showFilters) => filtersArray.length > 0 && showFilters,
);

export const selectActiveOptionsForAnalytics = (activeOptions) => {
  const resWithExtraSpace = activeOptions?.reduce(
    (accumulator, currentValue) => `${accumulator} ${currentValue}, `,
    '',
  );
  return resWithExtraSpace?.substring(1, resWithExtraSpace.length - 2);
};

// Значения фильтров для аналитики
export const selectAllFiltersForAnalytics = createSelector(
  getAllFiltersSettings,
  getFilterRangeMin,
  getFilterRangeMax,
  getCatalogHeadline,
  getCatalogTotalCount,
  (filters, rangeMin, rangeMax, catalogTitle, totalFoundItems) => {
    const result = [];
    Object.keys(filters)?.forEach((filterKey) => {
      if (filters[filterKey]?.activeOptions && filters[filterKey]?.activeOptions?.length > 0) {
        result.push({
          [filterKey]: selectActiveOptionsForAnalytics(filters[filterKey]?.activeOptions),
        });
      }

      if (
        Object.hasOwn(filters[filterKey], 'valueFrom') ||
        Object.hasOwn(filters[filterKey], 'valueTo')
      ) {
        result.push({
          [filterKey]: `${filters[filterKey]?.valueFrom || rangeMin} - ${
            filters[filterKey]?.valueTo || rangeMax
          }`,
        });
      }
    });
    return {
      title: catalogTitle,
      totalFoundItems,
      filters: result,
    };
  },
);
